import { rest, setupWorker, createResponseComposition } from "msw";
import { CadFeatureEnum } from "@mark43/cad-api";
import camelCase from "lodash/camelCase";
import deepmerge from "deepmerge";
import { QueryClient, QueryClientProvider as QueryClientProvider$1 } from "@tanstack/react-query";
import React, { useState, useEffect, useMemo, createContext, useContext } from "react";
import { createUseStream } from "@m43/sockets-mark43";
import isElectron from "is-electron";
import { DisManager } from "@m43/desktop-integration-service";
const ALL_TRUE = Object.keys(CadFeatureEnum).reduce((acc, key) => {
  acc[camelCase(key)] = true;
  return acc;
}, {});
function generateFakeEnabledFeatures(overrides = {}) {
  return {
    ...ALL_TRUE,
    ...overrides
  };
}
function createEnabledFeaturesHandler(overrides = {}) {
  return rest.get("/cad/api/bootstrap/feature_map", (_req, res, ctx) => {
    return res(ctx.status(200), ctx.json({ data: generateFakeEnabledFeatures(overrides) }));
  });
}
const handlers$1 = [createEnabledFeaturesHandler()];
const handlers = [...handlers$1];
const createWorkerControls = (handlers2) => {
  let currentWorker;
  return {
    startWorker: () => {
      console.log("Starting API Mock Worker");
      if (!currentWorker) {
        currentWorker = setupWorker(...handlers2);
        window.apiMockWorker = currentWorker;
      }
      currentWorker.start({
        serviceWorker: { url: "/cad-next/mockServiceWorker.js" },
        onUnhandledRequest(req) {
          console.warn(`Found an unhandled ${req.method} request to ${req.url.href}`);
        }
      });
    },
    stopWorker: () => {
      if (currentWorker) {
        console.log("Stopping API Mock Worker");
        currentWorker.stop();
      } else {
        console.log("API Mock Worker was not running when attempting to stop");
      }
    }
  };
};
const controls = createWorkerControls(handlers);
const getARandomNumber = () => {
  return Math.random() * 1e3;
};
const sleep = (timeMs) => {
  return new Promise((resolve) => {
    setTimeout(resolve, timeMs);
  });
};
const getCssTokenValue = (tokenName, defaultValue) => {
  if (!document) {
    return defaultValue;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(tokenName);
  return value !== "" ? value : defaultValue;
};
function override(first, second) {
  return deepmerge(first, second ?? {}, { arrayMerge: (_, sourceArray) => sourceArray });
}
const requestMethodToHandlerMethod = {
  GET: "get",
  POST: "post",
  PUT: "put",
  DELETE: "delete"
};
const createMergedResponse = (transformer) => {
  const resp = (originalResponse) => {
    try {
      const originalBody = JSON.parse(originalResponse.body);
      const newResponse = transformer(originalBody);
      if (newResponse) {
        return {
          ...originalResponse,
          ...newResponse,
          body: JSON.stringify(newResponse.response)
        };
      } else {
        return originalResponse;
      }
    } catch (e) {
      console.error(e);
      throw new Error("Failed to merge data into mock handler via `.with` call");
    }
  };
  return resp;
};
const storedResponseMap = /* @__PURE__ */ new Map();
const createStoredResponse = (url) => {
  const resp = (res) => {
    try {
      const parsedBody = JSON.parse(res.body);
      storedResponseMap.set(url, parsedBody);
      return res;
    } catch (e) {
      console.error(e);
      throw new Error("Failed to store data");
    }
  };
  return resp;
};
function mockHandler(params, implementation) {
  let useUrl;
  const url = params.url;
  switch (params.service) {
    case "RMS":
      useUrl = `/rms/api/${params.url}`;
      break;
    case "DEX":
      useUrl = `/dex/api/${params.url}`;
      break;
    case "USAGE_LOGS":
      useUrl = `/usage_logs/api/${params.url}`;
      break;
    case "NONE":
      useUrl = `/${params.url}`;
      break;
    default:
      useUrl = `/cad/api/${params.url}`;
      break;
  }
  const computedImplementation = !implementation ? (req, res, ctx) => {
    const resParams = [
      ctx.delay(params.delay ?? 0),
      ctx.status(params.status ?? 200),
      ctx.json(params.response ?? { success: true })
    ];
    if (params.once) {
      return res.once(...resParams);
    } else {
      return res(...resParams);
    }
  } : implementation;
  return Object.assign(
    rest[requestMethodToHandlerMethod[params.method]](useUrl, (req, res, ctx) => {
      return computedImplementation(
        req,
        createResponseComposition(void 0, [
          createStoredResponse(url)
        ]),
        ctx
      );
    }),
    {
      with: (withParams) => {
        if (typeof withParams === "function") {
          return mockHandler(params, (req, res, ctx) => {
            return computedImplementation(
              req,
              createResponseComposition(void 0, [
                createStoredResponse(url),
                createMergedResponse(withParams)
              ]),
              ctx
            );
          });
        } else {
          return mockHandler({ ...params, ...withParams });
        }
      },
      getLatestReturnValue: () => {
        return storedResponseMap.get(url);
      }
    }
  );
}
function createFakeDataWrapper(fakeDataGenerator) {
  return (amount, overrides) => {
    return new Array(amount).fill(0).map((_, index) => {
      if (Array.isArray(overrides)) {
        return fakeDataGenerator(index, overrides[index]);
      } else if (typeof overrides !== "undefined") {
        return fakeDataGenerator(index, overrides);
      } else {
        return fakeDataGenerator(index);
      }
    });
  };
}
let globalIdCounter = 0;
function createFaker(impl) {
  const faker = (overrideOrAmount, overrides) => {
    if (typeof overrideOrAmount === "undefined") {
      const ret = override(impl(), { id: ++globalIdCounter });
      return ret;
    } else if (typeof overrideOrAmount === "object") {
      const withoutOverride = impl();
      if (!overrideOrAmount.id) {
        overrideOrAmount.id = ++globalIdCounter;
      }
      const ret = override(withoutOverride, overrideOrAmount);
      return ret;
    } else {
      const arr = new Array(overrideOrAmount).fill(0).map((_, index) => {
        const withoutOverride = impl();
        if (Array.isArray(overrides)) {
          const overrideToUse = overrides[index] ?? {};
          if (!overrideToUse.id) {
            overrideToUse.id = ++globalIdCounter;
          }
          return override(withoutOverride, overrides[index]);
        } else {
          return override(withoutOverride, { id: ++globalIdCounter });
        }
      });
      return arr;
    }
  };
  return faker;
}
function augmentWithSearchValue(value) {
  const searchValue = Object.entries(value).map(([key, value2]) => {
    if (typeof value2 !== "string" && typeof value2 !== "number") {
      return void 0;
    }
    if (key === "id") {
      return void 0;
    }
    return value2 ? value2.toString().toLocaleLowerCase() : "";
  }).filter((value2) => !!value2).join("|");
  value.searchValue = searchValue;
  return value;
}
function createApiErrorHandler(handler) {
  return (e) => {
    if (e.response?.status === 400) {
      return e;
    } else {
      return handler(e);
    }
  };
}
const queryClientOptions = {
  defaultOptions: {
    mutations: {
      networkMode: "always",
      // Setting this to 0 will prevent `MutationCache` instances from hanging around and not being removed
      cacheTime: 0
    },
    // https://tanstack.com/query/v4/docs/reference/useQuery
    queries: {
      networkMode: "always",
      cacheTime: 0,
      // We set this to 'always' because the majority of our uses of useQuery are to hydrate the initial state of some modal/panel, and we know
      // that we always want to do this rather than possibly rely on an already cached value for the query.
      refetchOnMount: "always",
      // We set this to false but it may make sense to set this to true for "initial hydrate" type calls on something
      // like queues to counteract tab hibernation. Note that turning this to true will do nothing while staleTime
      // is set to Infinity.
      refetchOnWindowFocus: false,
      // We set this to false because we want control over our refetching and will typically do this based on socket
      // disconnect and reconnect. Similar to refetchOnWindowFocus, this will do nothing when set to true if staleTime
      // is set to Infinity.
      refetchOnReconnect: false,
      // This is set to Infinity but will need to be changed if we ever want to modify our default behaviour around the refetch
      // options above.
      staleTime: 0
    }
  }
};
const queryClient = new QueryClient(queryClientOptions);
const QueryClientProvider = ({
  children
}) => {
  return /* @__PURE__ */ React.createElement(QueryClientProvider$1, { client: queryClient }, children);
};
function useBroadcast(channelName, callback) {
  const [broadcastChannel] = useState(
    new BroadcastChannel(channelName)
  );
  useEffect(() => {
    let listener;
    if (callback) {
      listener = (event) => {
        const message = event.data;
        callback(message);
      };
      broadcastChannel.addEventListener("message", listener);
    }
    return () => {
      if (listener) {
        broadcastChannel.removeEventListener("message", listener);
      }
    };
  }, [broadcastChannel, callback, channelName]);
  return useMemo(() => {
    return {
      postMessage: (message) => {
        broadcastChannel.postMessage(message);
      }
    };
  }, [broadcastChannel]);
}
class Broadcast {
  constructor(channelName) {
    this.postMessage = (message) => {
      this.broadcastChannel.postMessage(message);
    };
    this.channelName = channelName;
    this.broadcastChannel = new BroadcastChannel(channelName);
  }
}
const ENABLE_DEV_SETTINGS_KEY = "enableDevSettings";
const DEV_SETTINGS_STORAGE_KEY = "devSettings";
const DEV_ENABLED_FEATURES_STORAGE_KEY = `${DEV_SETTINGS_STORAGE_KEY}.enabledFeatures`;
const DEV_LOAD_TESTS_STORAGE_KEY = `${DEV_SETTINGS_STORAGE_KEY}.loadTests`;
const globalWindow = typeof window === "undefined" ? void 0 : window;
const areDevSettingsEnabled = () => {
  return globalWindow?.localStorage?.getItem(ENABLE_DEV_SETTINGS_KEY) === "true";
};
const dumpDevSettings = () => {
  const settings = devSettingsProxy;
  return JSON.parse(JSON.stringify(settings));
};
const clearDevSettings = () => {
  if (!globalWindow) {
    return;
  }
  const confirmLocal = globalWindow?.confirm("Clear all dev settings in local storage?");
  if (confirmLocal) {
    globalWindow?.localStorage?.removeItem(DEV_SETTINGS_STORAGE_KEY);
    globalWindow?.localStorage?.removeItem(DEV_ENABLED_FEATURES_STORAGE_KEY);
    globalWindow?.localStorage?.removeItem(DEV_LOAD_TESTS_STORAGE_KEY);
  }
  globalWindow.enableDevSettings = false;
  globalWindow?.alert("Refresh browser to reset loaded settings.");
  return;
};
const printDevSettingsFromLocalStorage = (key) => {
  try {
    if (key === void 0 || key === "devSettings") {
      const localStored = JSON.parse(
        globalWindow?.localStorage?.getItem(DEV_SETTINGS_STORAGE_KEY) ?? "{}"
      );
      console.warn("localDevSettings", localStored);
    }
    if (key === void 0 || key === "enabledFeatures") {
      const localEnabledFeaturesStored = JSON.parse(
        globalWindow?.localStorage?.getItem(DEV_ENABLED_FEATURES_STORAGE_KEY) ?? "{}"
      );
      console.warn("localEnabledFeatures", localEnabledFeaturesStored);
    }
    if (key === void 0 || key === "loadTests") {
      const localLoadTestStored = JSON.parse(
        globalWindow?.localStorage?.getItem(DEV_LOAD_TESTS_STORAGE_KEY) ?? "{}"
      );
      console.warn("localLoadTest", localLoadTestStored);
    }
  } catch (e) {
    console.error("Failed to load dev settings from localStorage", e);
  }
};
const loadDevSettingsFromLocalStorage = () => {
  try {
    const localDevSettingsStored = JSON.parse(
      globalWindow?.localStorage?.getItem(DEV_SETTINGS_STORAGE_KEY) ?? "{}"
    );
    const localEnabledFeaturesStored = JSON.parse(
      globalWindow?.localStorage?.getItem(DEV_ENABLED_FEATURES_STORAGE_KEY) ?? "{}"
    );
    const localLoadTestStored = JSON.parse(
      globalWindow?.localStorage?.getItem(DEV_LOAD_TESTS_STORAGE_KEY) ?? "{}"
    );
    Object.assign(devSettingsProxy, localDevSettingsStored);
    Object.assign(enabledFeaturesProxy, localEnabledFeaturesStored);
    Object.assign(loadTestsProxy, localLoadTestStored);
  } catch (e) {
    console.error("Failed to load dev settings from localStorage", e);
  }
};
const storeDevSettingsInLocalStorage = () => {
  globalWindow?.localStorage?.setItem(ENABLE_DEV_SETTINGS_KEY, "true");
  const { enabledFeatures, loadTests, ...others } = devSettingsProxy;
  globalWindow?.localStorage?.setItem(DEV_SETTINGS_STORAGE_KEY, JSON.stringify(others));
  globalWindow?.localStorage?.setItem(
    DEV_ENABLED_FEATURES_STORAGE_KEY,
    JSON.stringify(enabledFeaturesProxy)
  );
  globalWindow?.localStorage?.setItem(DEV_LOAD_TESTS_STORAGE_KEY, JSON.stringify(loadTestsProxy));
};
const configureDevSettings = () => {
  if (!(globalWindow && areDevSettingsEnabled())) {
    return {
      devSettingsProxy: {},
      enabledFeaturesProxy: {},
      loadTestsProxy: {}
    };
  }
  const baseDefaultDevSettings = {
    enabledFeatures: {},
    loadTests: { map: { units: 0, events: 0, stations: 0, interval: 0 } },
    clear: clearDevSettings,
    dump: dumpDevSettings,
    print: printDevSettingsFromLocalStorage,
    load: loadDevSettingsFromLocalStorage,
    store: storeDevSettingsInLocalStorage
  };
  const storedDevSettings = JSON.parse(
    globalWindow?.localStorage.getItem(DEV_SETTINGS_STORAGE_KEY) || "{}"
  );
  storedDevSettings.enabledFeatures = JSON.parse(
    globalWindow?.localStorage.getItem(DEV_ENABLED_FEATURES_STORAGE_KEY) || "{}"
  );
  storedDevSettings.loadTests = JSON.parse(
    globalWindow?.localStorage.getItem(DEV_LOAD_TESTS_STORAGE_KEY) || "{}"
  );
  const enabledFeaturesProxy2 = new Proxy(baseDefaultDevSettings.enabledFeatures, {
    set(enabledFeatures, flag, value, receiver) {
      Reflect.set(enabledFeatures, flag, value, receiver);
      globalWindow?.localStorage.setItem(
        DEV_ENABLED_FEATURES_STORAGE_KEY,
        JSON.stringify(enabledFeatures)
      );
      return true;
    },
    get(enabledFeatures, flag, receiver) {
      return Reflect.get(enabledFeatures, flag, receiver);
    }
  });
  const loadTestsProxy2 = new Proxy(baseDefaultDevSettings.loadTests, {
    set(loadTests, flag, value, receiver) {
      Reflect.set(loadTests, flag, value, receiver);
      globalWindow?.localStorage.setItem(
        DEV_LOAD_TESTS_STORAGE_KEY,
        JSON.stringify(loadTests)
      );
      return true;
    },
    get(loadTests, flag, receiver) {
      return Reflect.get(loadTests, flag, receiver);
    }
  });
  const unsettableDevSettings = ["enabledFeatures", "loadTests"];
  const devSettingsProxy2 = new Proxy(baseDefaultDevSettings, {
    set(settings, providedSetting, value, receiver) {
      const setting = providedSetting;
      if (unsettableDevSettings.includes(setting)) {
        return false;
      }
      Reflect.set(settings, setting, value, receiver);
      if (typeof value === "function") {
        return true;
      }
      globalWindow?.localStorage.setItem(DEV_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      return true;
    },
    get: (settings, setting, receiver) => {
      switch (setting) {
        case "enabledFeatures":
          return enabledFeaturesProxy2;
        case "loadTests":
          return loadTestsProxy2;
        default:
          return Reflect.get(settings, setting, receiver);
      }
    }
  });
  return {
    devSettingsProxy: devSettingsProxy2,
    enabledFeaturesProxy: enabledFeaturesProxy2,
    loadTestsProxy: loadTestsProxy2
  };
};
const { devSettingsProxy, enabledFeaturesProxy, loadTestsProxy } = configureDevSettings();
const getDevSetting = (setting, defaultValue) => {
  if (!globalWindow?.enableDevSettings) {
    return void 0;
  }
  return globalWindow?.devSettings?.[setting] ?? defaultValue;
};
const getDevAllEnabledFeatures = () => {
  return globalWindow?.enableDevSettings ? globalWindow?.devSettings?.enabledFeatures : void 0;
};
const getDevEnabledFeature = (key) => {
  return globalWindow?.enableDevSettings ? globalWindow?.devSettings?.enabledFeatures?.[key] : void 0;
};
const getLoadTestsConfig = (key) => {
  if (globalWindow?.enableDevSettings) {
    if (key) {
      return globalWindow?.devSettings?.loadTests?.[key];
    }
    return globalWindow?.devSettings?.loadTests;
  }
  return void 0;
};
const setDevSettings = () => {
  if (!globalWindow) {
    return;
  }
  const devSettingsConfig = configureDevSettings();
  globalWindow.devSettings = devSettingsConfig.devSettingsProxy;
  if (globalWindow && areDevSettingsEnabled()) {
    globalWindow.devSettings?.load?.();
  }
};
if (globalWindow && !globalWindow?.hasOwnProperty("enableDevSettings")) {
  Object.defineProperty(globalWindow, "enableDevSettings", {
    set(value) {
      globalWindow?.localStorage.setItem(ENABLE_DEV_SETTINGS_KEY, value ? "true" : "false");
      setDevSettings();
    },
    get() {
      if (!globalWindow?.devSettings) {
        setDevSettings();
      }
      return Object.getOwnPropertyNames(globalWindow?.devSettings).length > 0 || void 0;
    }
  });
}
var AppName = /* @__PURE__ */ ((AppName2) => {
  AppName2["CAD_NEXT"] = "cad-next";
  AppName2["CAD_CLIENT"] = "cad-client";
  AppName2["FR_CLIENT"] = "fr-client";
  return AppName2;
})(AppName || {});
const AppNameContext = createContext(void 0);
const AppNameProvider = ({
  appName,
  children
}) => {
  return /* @__PURE__ */ React.createElement(AppNameContext.Provider, { value: appName }, children);
};
const useAppName = () => {
  const appName = useContext(AppNameContext);
  if (!appName) throw new Error("Couldn't find AppNameContext");
  return appName;
};
function useIsLegacyCad() {
  const appName = useAppName();
  return appName === "cad-client";
}
function useIsFrClient() {
  const appName = useAppName();
  return appName === "fr-client";
}
const useStream = createUseStream();
const createStreamListener = (streamListener) => {
  return streamListener;
};
const getRecordKeys = (object) => {
  return Object.keys(object);
};
const getRecordValues = (object) => {
  return Object.values(object);
};
const getRecordEntries = (object) => {
  return Object.entries(object);
};
let metricTags;
function getMetricTags() {
  return metricTags;
}
function assignMetricTags(partialMetricTags) {
  const replacement = Object.assign({}, metricTags, partialMetricTags);
  metricTags = replacement;
}
function metricTagsToHeaders() {
  const headers = {
    "x-client-session-id": metricTags.client_session_id,
    "x-client-version": metricTags.client_version,
    "x-application-name": metricTags.application_name,
    "x-is-electron": metricTags.is_electron
  };
  if (isElectron()) {
    if (metricTags.client_window_id) {
      headers["x-client-window-id"] = metricTags.client_window_id;
    }
    if (metricTags.cad_workspace_window_type) {
      headers["x-cad-workspace-window-type"] = metricTags.cad_workspace_window_type;
    }
    if (metricTags.electron_main_version) {
      headers["x-electron-main-version"] = metricTags.electron_main_version;
    }
  }
  return headers;
}
const disManager = new DisManager();
const originalXHR = window.XMLHttpRequest;
const PatchedXMLHttpRequest = (function() {
  const xhr = new originalXHR();
  const originalOpen = xhr.open;
  const originalSetRequestHeader = xhr.setRequestHeader;
  const patchedOpen = function(method, url, async, username, password) {
    this._httpMethod = method;
    if (arguments.length === 2) {
      return originalOpen.call(this, method, url);
    }
    return originalOpen.call(this, method, url, async, username, password);
  };
  const patchedSetRequestHeader = function(name, value) {
    if (!this._httpHeaders) {
      this._httpHeaders = {};
    }
    this._httpHeaders[name.toLowerCase()] = value;
    return originalSetRequestHeader.call(this, name, value);
  };
  xhr.open = patchedOpen;
  xhr.setRequestHeader = patchedSetRequestHeader;
  return xhr;
});
PatchedXMLHttpRequest.prototype = originalXHR.prototype;
window.XMLHttpRequest = PatchedXMLHttpRequest;
function getXHRMethod(xhr) {
  return xhr._httpMethod || "METHOD_UNKNOWN";
}
function getXHRHeaders(xhr) {
  return xhr._httpHeaders || {};
}
export {
  AppName,
  AppNameContext,
  AppNameProvider,
  Broadcast,
  QueryClientProvider,
  assignMetricTags,
  augmentWithSearchValue,
  controls,
  createApiErrorHandler,
  createEnabledFeaturesHandler,
  createFakeDataWrapper,
  createFaker,
  createStreamListener,
  createWorkerControls,
  devSettingsProxy,
  disManager,
  enabledFeaturesProxy,
  generateFakeEnabledFeatures,
  getARandomNumber,
  getCssTokenValue,
  getDevAllEnabledFeatures,
  getDevEnabledFeature,
  getDevSetting,
  getLoadTestsConfig,
  getMetricTags,
  getRecordEntries,
  getRecordKeys,
  getRecordValues,
  getXHRHeaders,
  getXHRMethod,
  handlers,
  loadTestsProxy,
  metricTagsToHeaders,
  mockHandler,
  override,
  queryClient,
  queryClientOptions,
  sleep,
  useAppName,
  useBroadcast,
  useIsFrClient,
  useIsLegacyCad,
  useStream
};
